import { useEffect, useRef, useState } from 'react'
import { Ban, CheckCircle, CreditCard, HelpCircle, Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import type { Invoice } from '@/types/invoice'
import { parseGermanNumber } from '@/lib/utils'
import { formatCurrency, formatDate } from './invoiceUtils'

interface Props {
  invoice: Invoice
  openSum: number
  onStatus: (status: string, note?: string | null) => void
  onPay: (amount: number) => void
  onDelete: () => void
  pending: { pay: boolean; status: boolean; remove: boolean }
}

type Pop = 'pay' | 'clarify' | 'delete' | null

/**
 * Gepinnte Aktionsleiste.
 *
 * Die Eingaben zu Zahlung, Klaerung und Loeschen liegen in einem Popover UEBER der
 * Leiste — im alten Modal standen sie im Scrollbereich und waren nach einem Klick
 * regelmaessig ausserhalb des Sichtfelds.
 */
export default function InvoiceActionBar({
  invoice, openSum, onStatus, onPay, onDelete, pending,
}: Props) {
  const [pop, setPop] = useState<Pop>(null)
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [confirmOver, setConfirmOver] = useState(false)
  const popRef = useRef<HTMLDivElement>(null)

  const isReminder = invoice.type === 'reminder'
  // Ausgangsrechnungen kennen keinen Freigabe-Workflow (Geprüft → Bitte zahlen):
  // gezahlt wird an uns, offen ist nur der Zahlungseingang.
  const isOutgoing = invoice.type === 'outgoing'
  const isClosed = ['paid', 'resolved', 'rejected'].includes(invoice.status)

  // Beleg gewechselt → Popover schliessen, sonst wirkt es auf den falschen Datensatz
  useEffect(() => { setPop(null) }, [invoice.id])

  useEffect(() => {
    if (!pop) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setPop(null) } }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [pop])

  const openPay = () => {
    // #107: Bei ausgeglichenen Belegen/Gutschriften ist 0 erlaubt und markiert als verrechnet.
    setAmount(openSum > 0 ? openSum.toFixed(2).replace('.', ',') : '0,00')
    setConfirmOver(false)
    setPop('pay')
  }

  const submitPay = () => {
    if (pending.pay) return
    const value = parseGermanNumber(amount)
    const settleOnly = openSum <= 0.005
    if (value == null || (value <= 0 && !settleOnly)) {
      toast.error('Bitte einen gültigen Betrag eingeben')
      return
    }
    if (!settleOnly && value > openSum + 0.005 && !confirmOver) {
      setConfirmOver(true)
      return
    }
    onPay(value)
    setPop(null)
  }

  const btn = 'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50'
  const btnQuiet = `${btn} border-gray-200 bg-white text-gray-600 hover:border-gray-400 hover:text-gray-900`
  const btnDanger = `${btn} border-red-200 bg-white text-red-600 hover:border-red-500 hover:bg-red-50`

  const typed = parseGermanNumber(amount)
  const isPartial = typed != null && openSum > 0.005 && typed > 0 && typed < openSum - 0.005

  return (
    <div className="relative flex flex-shrink-0 flex-wrap items-center gap-2 border-t border-gray-200 bg-gray-50 px-4 py-2.5">
      {/* Statushinweis für abgeschlossene Belege */}
      {invoice.status === 'paid' && (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
          <CheckCircle className="h-3.5 w-3.5" />
          Bezahlt{invoice.paid_at && ` am ${formatDate(invoice.paid_at)}`}
        </span>
      )}
      {invoice.status === 'resolved' && (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
          <CheckCircle className="h-3.5 w-3.5" /> Erledigt
        </span>
      )}
      {invoice.status === 'rejected' && (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800">
          <Ban className="h-3.5 w-3.5" /> Abgelehnt
        </span>
      )}

      {!isClosed && !isReminder && invoice.status !== 'clarification' && (
        <button type="button" className={btnQuiet} onClick={() => { setNote(''); setPop('clarify') }}>
          <HelpCircle className="h-3.5 w-3.5" /> Klärung
        </button>
      )}

      <button type="button" className={btnDanger} onClick={() => setPop('delete')}>
        <Trash2 className="h-3.5 w-3.5" /> Aus Buchhaltung entfernen
      </button>

      <span className="flex-1" />

      {!isClosed && !isReminder && (
        <>
          {invoice.status === 'new' && !isOutgoing && (
            <button type="button" disabled={pending.status} className={`${btn} border-primary-500 bg-primary-500 text-white hover:bg-primary-600`} onClick={() => onStatus('reviewed')}>
              <CheckCircle className="h-3.5 w-3.5" /> Geprüft
            </button>
          )}
          {(invoice.status === 'reviewed' || invoice.status === 'clarification') && !isOutgoing && (
            <button type="button" disabled={pending.status} className={`${btn} border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600`} onClick={() => onStatus('pay')}>
              <CreditCard className="h-3.5 w-3.5" /> Bitte zahlen
            </button>
          )}
          <button type="button" className={btnQuiet} onClick={openPay}>
            <CreditCard className="h-3.5 w-3.5" /> Teilzahlung
          </button>
          <button type="button" className={`${btn} border-green-700 bg-green-700 text-white hover:bg-green-800`} onClick={openPay}>
            <CheckCircle className="h-3.5 w-3.5" /> Bezahlt · {formatCurrency(Math.max(0, openSum))}
          </button>
        </>
      )}

      {/* Eine Mahnung wird nicht bezahlt — bezahlt wird die Rechnung dahinter.
          Sie kennt genau einen Abschluss: "Erledigt". Bis 06.08.2026 stand hier
          zusaetzlich ein "Bezahlt"-Knopf; weil "Erledigt" serverseitig gar nicht
          durchging, landeten Mahnungen ueber diesen Umweg im Rechnungs-Status
          "bezahlt" (#122/#123). Erledigen aendert NUR diese Mahnung — weder die
          zugeordneten Rechnungen noch Folge-Mahnungen. */}
      {!isClosed && isReminder && (
        <>
          {/* #134: "Geprüft" gibt es jetzt auch fuer Mahnungen — es haelt nur fest,
              dass jemand draufgeschaut hat, und aendert nichts an den zugeordneten
              Rechnungen. Serverseitig war der Status ('reviewed') fuer Mahnungen
              schon immer erlaubt, nur der Knopf fehlte. */}
          {invoice.status === 'new' && (
            <button
              type="button"
              disabled={pending.status}
              title="Vermerkt, dass die Mahnung geprüft wurde — sie bleibt offen"
              className={`${btn} border-primary-500 bg-primary-500 text-white hover:bg-primary-600`}
              onClick={() => onStatus('reviewed')}
            >
              <CheckCircle className="h-3.5 w-3.5" /> Geprüft
            </button>
          )}
          <button
            type="button"
            disabled={pending.status}
            title="Legt nur diese Mahnung ab — Rechnungen und weitere Mahnungen bleiben unberührt"
            className={`${btn} border-green-700 bg-green-700 text-white hover:bg-green-800`}
            onClick={() => onStatus('resolved')}
          >
            <CheckCircle className="h-3.5 w-3.5" /> Erledigt
          </button>
        </>
      )}

      {/* ── Popover über der Leiste ── */}
      {pop && (
        <div
          ref={popRef}
          className="absolute bottom-[calc(100%+8px)] right-4 z-10 w-[min(380px,calc(100vw-3rem))] space-y-2.5 rounded-lg border border-gray-200 bg-white p-3 shadow-xl"
          role="dialog"
        >
          {pop === 'pay' && (
            <>
              <h4 className="text-sm font-bold text-gray-900">Zahlbetrag erfassen</h4>
              {openSum <= 0.005 ? (
                <p className="text-xs text-green-700">
                  Kein offener Betrag — „Zahlung buchen" markiert {invoice.type === 'credit_note' ? 'die Gutschrift' : 'den Beleg'} als bezahlt/verrechnet.
                </p>
              ) : (
                <p className="text-xs text-gray-600">
                  Offen: <b className="tabular-nums">{formatCurrency(openSum)}</b>. Ein kleinerer Betrag wird als Teilzahlung gebucht.
                </p>
              )}
              <input
                autoFocus
                type="text"
                inputMode="decimal"
                value={amount}
                aria-label="Zahlbetrag"
                onChange={(e) => { setAmount(e.target.value); setConfirmOver(false) }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitPay() } }}
                className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-right font-bold tabular-nums focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
              {isPartial && (
                <p className="text-xs text-amber-700">
                  Teilzahlung — die Rechnung bleibt offen, Restbetrag:{' '}
                  <strong className="tabular-nums">{formatCurrency(Math.round((openSum - (typed || 0)) * 100) / 100)}</strong>
                </p>
              )}
              {confirmOver && (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800">
                  Der Betrag übersteigt die offene Summe. Nochmal klicken, um zu buchen.
                </p>
              )}
              <div className="flex justify-end gap-2">
                <button type="button" className={btnQuiet} onClick={() => setPop(null)}>Abbrechen</button>
                <button type="button" disabled={pending.pay} className={`${btn} border-green-700 bg-green-700 text-white hover:bg-green-800`} onClick={submitPay}>
                  {pending.pay && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {confirmOver ? 'Ja, weiter' : 'Zahlung buchen'}
                </button>
              </div>
            </>
          )}

          {pop === 'clarify' && (
            <>
              <h4 className="text-sm font-bold text-gray-900">Zur Klärung stellen</h4>
              <p className="text-xs text-gray-600">Der Grund erscheint als Hinweis in der Rechnung.</p>
              <textarea
                autoFocus
                rows={3}
                value={note}
                aria-label="Klärungsbedarf"
                onChange={(e) => setNote(e.target.value)}
                placeholder="Was muss geklärt werden?"
                className="w-full rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
              />
              <div className="flex justify-end gap-2">
                <button type="button" className={btnQuiet} onClick={() => setPop(null)}>Abbrechen</button>
                <button
                  type="button"
                  className={`${btn} border-orange-500 bg-orange-500 text-white hover:bg-orange-600`}
                  onClick={() => { onStatus('clarification', note); setPop(null) }}
                >
                  Klärung anfordern
                </button>
              </div>
            </>
          )}

          {pop === 'delete' && (
            <>
              <h4 className="text-sm font-bold text-gray-900">
                {isReminder ? 'Mahnung' : 'Rechnung'} {invoice.invoice_number || `#${invoice.id}`} aus der Buchhaltung entfernen?
              </h4>
              <p className="text-xs text-gray-600">
                Der Beleg bleibt in der Dokumentenablage und erscheint dort wieder als „Offen" —
                er kann jederzeit erneut übernommen werden. Gelöscht wird nur die Buchung.
              </p>
              <div className="flex justify-end gap-2">
                <button type="button" className={btnQuiet} onClick={() => setPop(null)}>Abbrechen</button>
                <button type="button" disabled={pending.remove} className={`${btn} border-red-600 bg-red-600 text-white hover:bg-red-700`} onClick={onDelete}>
                  {pending.remove && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Entfernen
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
